import React, { useState } from "react";
import { Building2, TrendingUp, TrendingDown, Camera, AlertTriangle, CheckCircle, MoreHorizontal, X, Minimize2, Maximize2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { FilterState } from "@/pages/Reports";

interface WorksComparisonPanelProps {
  filters: FilterState;
  onMinimize?: () => void;
  onClose?: () => void;
  isMinimized?: boolean;
}

interface WorkComparisonItem {
  id: string;
  name: string;
  progress: number;
  status: string;
  tasksOverdue: number;
  incidents: number;
  photos: number;
  videos: number;
  trend: 'up' | 'down';
  trendValue: string;
  budget: string;
  deadline: string;
  manager: string;
}

// Sem dados de exemplo: os comparativos passarão a ser agregados dos RDOs
// reais das obras (Supabase). Até lá, o painel exibe o estado vazio abaixo.
const worksData: WorkComparisonItem[] = [];

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'on_track':
      return {
        label: 'No Prazo',
        color: 'bg-green-500',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
      };
    case 'ahead':
      return {
        label: 'Adiantado',
        color: 'bg-blue-500',
        textColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
      };
    case 'at_risk':
      return {
        label: 'Em Risco',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
      };
    case 'delayed':
      return {
        label: 'Atrasado',
        color: 'bg-red-500',
        textColor: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
      };
    default:
      return {
        label: 'Indefinido',
        color: 'bg-gray-500',
        textColor: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
      };
  }
};

export const WorksComparisonPanel = ({ filters, onMinimize, onClose, isMinimized = false }: WorksComparisonPanelProps) => {
  const [viewMode, setViewMode] = useState<'grid' | 'comparison'>('grid');
  const [internalMinimized, setInternalMinimized] = useState(false);

  const minimized = isMinimized || internalMinimized;

  const handleMinimize = () => {
    if (onMinimize) {
      onMinimize();
    } else {
      setInternalMinimized(!internalMinimized);
    }
  };

  return (
    <Card className={`${minimized ? 'p-4' : 'p-6'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Building2 className="h-6 w-6 text-primary shrink-0" />
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-semibold">
              Painel de Obras
            </h2>
            {!minimized && (
              <p className="text-muted-foreground">Comparativo de performance e status</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!minimized && (
            <div className="flex gap-2">
              <button 
                onClick={() => setViewMode('grid')}
                className={`min-h-11 px-3 py-1 rounded-md text-sm font-medium transition-colors sm:min-h-8 ${
                  viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Visão Geral
              </button>
              <button 
                onClick={() => setViewMode('comparison')}
                className={`min-h-11 px-3 py-1 rounded-md text-sm font-medium transition-colors sm:min-h-8 ${
                  viewMode === 'comparison' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Comparativo
              </button>
            </div>
          )}
          <button
            onClick={handleMinimize}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            {minimized ? (
              <Maximize2 className="h-4 w-4 text-gray-500" />
            ) : (
              <Minimize2 className="h-4 w-4 text-gray-500" />
            )}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      
      {!minimized && worksData.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground gap-3">
          <Building2 className="h-10 w-10 opacity-40" />
          <div>
            <p className="font-medium text-foreground">Sem dados comparativos ainda</p>
            <p className="text-sm">
              Os comparativos de performance serão calculados a partir dos RDOs das suas obras.
            </p>
          </div>
        </div>
      )}

      {!minimized && worksData.length > 0 && (
        <>
          {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {worksData.map((obra) => {
            const statusConfig = getStatusConfig(obra.status);
            return (
              <Card 
                key={obra.id} 
                className={`p-6 hover:shadow-lg transition-all duration-300 cursor-pointer ${statusConfig.bgColor} ${statusConfig.borderColor} border-l-4`}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground leading-tight">
                        {obra.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {obra.manager}
                      </p>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={`${statusConfig.bgColor} ${statusConfig.textColor} border-0`}
                    >
                      {statusConfig.label}
                    </Badge>
                  </div>

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Progresso Físico</span>
                      <div className="flex items-center gap-1">
                        <span className="text-lg font-bold">{obra.progress}%</span>
                        <Badge 
                          variant={obra.trend === 'up' ? 'default' : 'destructive'}
                          className="text-xs gap-1"
                        >
                          {obra.trend === 'up' ? 
                            <TrendingUp className="h-3 w-3" /> : 
                            <TrendingDown className="h-3 w-3" />
                          }
                          {obra.trendValue}
                        </Badge>
                      </div>
                    </div>
                    <Progress value={obra.progress} className="h-2" />
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      <span className="text-muted-foreground">Atrasos:</span>
                      <span className="font-medium">{obra.tasksOverdue}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-red-500" />
                      <span className="text-muted-foreground">Incidentes:</span>
                      <span className="font-medium">{obra.incidents}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4 text-blue-500" />
                      <span className="text-muted-foreground">Fotos:</span>
                      <span className="font-medium">{obra.photos}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4 text-purple-500" />
                      <span className="text-muted-foreground">Vídeos:</span>
                      <span className="font-medium">{obra.videos}</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="pt-2 border-t border-border/50">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Orçamento: {obra.budget}</span>
                      <span>Prazo: {new Date(obra.deadline).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
          )}

          {viewMode === 'comparison' && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4 font-semibold">Obra</th>
                <th className="text-center p-4 font-semibold">Status</th>
                <th className="text-center p-4 font-semibold">Progresso</th>
                <th className="text-center p-4 font-semibold">Atrasos</th>
                <th className="text-center p-4 font-semibold">Incidentes</th>
                <th className="text-center p-4 font-semibold">Registros</th>
                <th className="text-center p-4 font-semibold">Tendência</th>
                <th className="text-center p-4 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {worksData.map((obra) => {
                const statusConfig = getStatusConfig(obra.status);
                return (
                  <tr key={obra.id} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="p-4">
                      <div>
                        <div className="font-medium">{obra.name}</div>
                        <div className="text-sm text-muted-foreground">{obra.manager}</div>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <Badge className={`${statusConfig.bgColor} ${statusConfig.textColor} border-0`}>
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center gap-2 justify-center">
                        <div className="w-16">
                          <Progress value={obra.progress} className="h-2" />
                        </div>
                        <span className="font-medium">{obra.progress}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`font-medium ${obra.tasksOverdue > 5 ? 'text-red-600' : 'text-foreground'}`}>
                        {obra.tasksOverdue}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`font-medium ${obra.incidents > 2 ? 'text-red-600' : 'text-foreground'}`}>
                        {obra.incidents}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="text-sm">
                        <div>{obra.photos} fotos</div>
                        <div className="text-muted-foreground">{obra.videos} vídeos</div>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <Badge 
                        variant={obra.trend === 'up' ? 'default' : 'destructive'}
                        className="gap-1"
                      >
                        {obra.trend === 'up' ? 
                          <TrendingUp className="h-3 w-3" /> : 
                          <TrendingDown className="h-3 w-3" />
                        }
                        {obra.trendValue}
                      </Badge>
                    </td>
                    <td className="p-4 text-center">
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}
    </Card>
  );
};